import { styled, alpha } from '@mui/material/styles';
import { Drawer, Box, Typography, TypographyProps, IconButton, Button, Card } from '@mui/material';
import { spacing } from '../../../../styles/theme';

// Drawer principal
export const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: { xs: '100%', sm: '1000px', md: '1200px' },
    maxWidth: '1200px',
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'none',
    color: theme.palette.text.primary,
  },
}));

// Container principal
export const DrawerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
}));

// Header del drawer
export const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingLeft: `${spacing.lg}px`,
  paddingRight: `${spacing.lg}px`,
  paddingTop: `${spacing.lg}px`,
  paddingBottom: `${spacing.lg}px`,
  backgroundColor: theme.palette.background.paper,
  boxSizing: 'border-box',
}));

// Título del header
export const DrawerTitle = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  margin: 0,
  padding: 0,
}));

// Botón de cerrar
export const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Barra de progreso
export const ProgressContainer = styled(Box)(({ theme }) => ({
  paddingLeft: `${spacing.lg}px`,
  paddingRight: `${spacing.lg}px`,
  paddingTop: `${spacing.md}px`,
  paddingBottom: `${spacing.md}px`,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxSizing: 'border-box',
}));

export const ProgressBar = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
  borderRadius: spacing.xs,
  height: 8,
  overflow: 'hidden',
  boxSizing: 'border-box',
}));

export const ProgressBarFill = styled(Box)(() => ({
  height: '100%',
  borderRadius: spacing.xs,
  background: `linear-gradient(to right, #7C5DC9, #5084EB, #0CBBDD, #0CBB9A, #10B981)`,
  transition: 'width 0.5s ease-in-out',
}));

export const ProgressInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '14px',
  color: theme.palette.text.secondary,
  marginTop: spacing.sm,
}));

// Container del contenido
export const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  padding: 0,
  backgroundColor: theme.palette.background.paper,
}));

export const StepScrollArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: spacing.lg,
  backgroundColor: theme.palette.background.paper,
}));

export const ConfirmationLayout = styled(Box)({
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
});

export const ConfirmationLeftColumn = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  padding: spacing.lg,
  overflow: 'auto',
  backgroundColor: theme.palette.background.paper,
}));

export const ConfirmationRightColumn = styled(Box)(({ theme }) => ({
  width: 400,
  flexShrink: 0,
  padding: spacing.lg,
  overflow: 'auto',
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
  borderLeft: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
}));

// Footer del drawer
export const DrawerFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: spacing.lg,
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[50],
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spacing.md,
}));

export const FooterButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: spacing.md,
}));

// Color/fondo/hover vienen de variant="tonal" del tema; aquí solo tamaño/spacing.
export const PreviousButton = styled(Button)({
  minWidth: '120px',
  padding: '9px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
});

export const NextButton = styled(Button)(({ theme }) => ({
  minWidth: '150px',
  padding: '9px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  background: `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.success.main})`,
  color: theme.palette.common.white,
  '&:hover': {
    background: `linear-gradient(to right, ${theme.palette.info.dark}, ${theme.palette.success.dark})`,
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

export const ConfirmButton = styled(Button)(({ theme }) => ({
  minWidth: '180px',
  padding: '9px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  background: `linear-gradient(to right, ${theme.palette.success.main}, ${theme.palette.info.main})`,
  color: theme.palette.common.white,
  '&:hover': {
    background: `linear-gradient(to right, ${theme.palette.success.dark}, ${theme.palette.info.dark})`,
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

export const DashboardCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`
        : '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
}));

export const SectionBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  overflow: 'hidden',
  marginBottom: '24px',
  backgroundColor: theme.palette.background.paper,
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '16px 20px',
  background:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.12)
      : 'linear-gradient(90deg, rgba(59,130,246,0.10) 0%, rgba(59,130,246,0.04) 100%)',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));
